/** @jsxImportSource theme-ui */
import { Box, Button, Container, Spinner, Text } from "theme-ui";
import Logo from "../components/Logo";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import SingleResult from "../components/SingleResult";
import { ImageRequest } from "../types/imagerequest";
import apiInvocation from "../api/apiInvocation";
import Tooltip from "../components/common/Tooltip";
import Icon from "../components/common/Icon";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { DatePicker } from "antd";
import "antd/dist/reset.css"; // Import Ant Design styles
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import SitesModal from "../components/SitesModal";

const { RangePicker } = DatePicker;
dayjs.extend(utc);

const Admin = () => {
  const [allRequests, setAllRequests] = useState<ImageRequest[]>([]);
  const [isFetchingRequests, setIsFetchingRequests] = useState(false);
  const [isSitesModalOpen, setIsSitesModalOpen] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDateFrom, setSelectedDateFrom] = useState<string | null>(null);
  const [selectedDateTo, setSelectedDateTo] = useState<string | null>(null);
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const isAdmin = activeAccount?.idTokenClaims?.roles?.includes("Admin");
  const navigate = useNavigate();

  // Handler to remove deleted item from the list
  const handleDeleteSuccess = (deletedId: string) => {
    setAllRequests((prev) => prev.filter((request) => request.id !== deletedId));
  };

  const onDownload = async () => {
    try {
      // Fetch the Blob from the API
      const csvBlob = await apiInvocation.getCsvFile();

      // Create a URL for the Blob
      const blobUrl = URL.createObjectURL(csvBlob);

      // Generate the filename with the current timestamp
      const timestamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
      const fileName = `biosnap-${timestamp}.csv`;

      // Create a temporary anchor element
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName; // Use the generated filename
      a.style.display = "none"; // Hide the anchor element

      // Append the anchor to the document, trigger the click, and then remove it
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Revoke the Blob URL to free up memory
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading the CSV file:", error);
    }
  };

  const fetchRequests = async (page: number) => {
    if (isFetchingRequests) return;

    setIsFetchingRequests(true);
    try {
      const response = await apiInvocation.getAllImagesPaginated(
          true,
          page,
          10,
          selectedDateFrom,
          selectedDateTo
      );

      if (response.items.length === 0 || page >= response.totalPages) {
        setHasMore(false);
      }

      setAllRequests((prev) => [
        ...prev,
        ...response.items.filter((item) => !prev.some((r) => r.id === item.id)),
      ]);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setIsFetchingRequests(false);
    }
  };

  const handleDateChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null, dateStrings: [string, string]) => {
    if (!dates || !dates[0] || !dates[1]) {
      // Reset both dates when cleared
      setSelectedDateFrom(null);
      setSelectedDateTo(null);
      setPageNumber(1);
      setAllRequests([]);
      setHasMore(true);
      return;
    }

    // Preserve the local date, only adjust time (00:00 and 23:59)
    const formattedStartDate = dates[0].startOf("day").format("YYYY-MM-DDTHH:mm:ss");
    const formattedEndDate = dates[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss");

    setSelectedDateFrom(formattedStartDate);
    setSelectedDateTo(formattedEndDate);

    // Reset pagination and fetch new filtered data
    setPageNumber(1);
    setAllRequests([]);
    setHasMore(true);
  };

  useEffect(() => {
    // Fetch all requests if both dates are null (i.e., initial state)
    if (!selectedDateFrom && !selectedDateTo) {
      fetchRequests(pageNumber);
    }
    // Fetch filtered requests only when both dates are selected
    else if (selectedDateFrom && selectedDateTo) {
      fetchRequests(pageNumber);
    }
  }, [selectedDateFrom, selectedDateTo, pageNumber]);

  const loadMore = () => {
    if (!isFetchingRequests && hasMore) {
      setPageNumber((prev) => prev + 1);
    }
  };

  return (
      <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center", // Add this for proper centering
            width: "100%", // Ensure full width
            minHeight: "100vh",
            overflowY: "auto",
          }}
      >
        <Logo />

        <Text sx={{ fontSize: [5, 7], fontWeight: "semiBold", mb: 2 }}>
          User's Submissions
        </Text>
        <Text sx={{ fontSize: [1, 5], color: "secondaryText" }}>
          Take a look at all user's submissions.
        </Text>

        <Box sx={{
          width: "100%",
          maxWidth: 300,
          mb: 5,
          mt: 5
        }}>
          <RangePicker
              style={{
                width: "100%",
                fontSize: "16px",
                padding: "8px",
              }}
              placeholder={["Date from", "Date to"]}
              placement="bottomLeft"
              dropdownClassName="custom-rangepicker-dropdown"
              onChange={handleDateChange}
              format="YYYY-MM-DD"
          />
        </Box>

        {isFetchingRequests && allRequests.length === 0 ? (
            <Spinner
                size={24}
                sx={{ color: "primaryText", mt: [9, 10], height: "44px" }}
            />
        ) : (
            <InfiniteScroll
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  minHeight: "100vh",
                  overflowY: "auto",
                }}
                dataLength={allRequests.length}
                next={loadMore}
                hasMore={hasMore}
                loader={
                  <Spinner
                      size={24}
                      sx={{ color: "primaryText", mt: [2, 3], height: "32px" }}
                  />
                }
            >
              {allRequests.map((r) => (
                  <SingleResult
                      key={r.id}
                      result={r}
                      isAdmin
                      onDeleteSuccess={handleDeleteSuccess}
                  />
              ))}
            </InfiniteScroll>
        )}

        <Box sx={{ mt: 13 }} />

        <Box
            sx={{
              position: "fixed",
              bottom: [20, 60],
              right: [20, 60],
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
        >
          <Box>
            <Tooltip content="Download data">
              <Button
                  variant="primary"
                  sx={{
                    width: 60,
                    height: 60,
                    border: "2px solid #F5F9FC",

                  }}
                  onClick={onDownload}
              >
                <Icon
                    name="box-add"
                    color="buttonText"
                    size={24}

                />
              </Button>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip content="Manage sites">
              <Button
                  variant="primary"
                  sx={{
                    width: 60,
                    height: 60,
                    border: "2px solid #F5F9FC",
                  }}
                  onClick={() => setIsSitesModalOpen(true)}
              >
                <Icon
                    name="compass"
                    color="buttonText"
                    size={24}
                />
              </Button>
            </Tooltip>
          </Box>
          <Box>
            <Tooltip content="Home page">
              <Button
                  variant="primary"
                  sx={{
                    width: 60,
                    height: 60,
                    border: "2px solid #F5F9FC", // Adds border
                  }}
              >
                <Icon
                    name="home"
                    color="buttonText"
                    size={24}
                    onClick={() => navigate("/")}
                />
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <SitesModal
            isOpen={isSitesModalOpen}
            onClose={() => setIsSitesModalOpen(false)}
        />

      </Container>
  );
};

export default Admin;